/**
 * Unified Heather Smart Card Action Execution Controller.
 * 
 * Handles execution of all actionable Smart Card recipes emitted by Heather's
 * Python tools across all app surfaces (Heather full page, Overlay, Cockpit HUD,
 * Cockpit Controls, and Global Team Chat).
 * 
 * Includes:
 * - Pre-flight conflict & data staleness detection
 * - Offline-first UnitOfWork & Service atomic transactions
 * - Maintenance, Time Clock, Logistics, Schedule, Customer, Location & Quote operations
 * - Execution state transitions (draft -> executing -> approved/rejected/conflict)
 * - Confirmation loop closing back to Heather
 */

import type { AppRouterInstance } from "next/dist/shared/lib/app-router-context.shared-runtime";
import type { AgentMessage } from "@/lib/state/agent-sync";
import { agentSync, parseSmartCard } from "@/lib/state/agent-sync";
import { getDashboardCreatePath } from "@/lib/routes/dashboard-routes";
import type { UnitOfWork } from "@/lib/data/repo";
import { TimeClockService } from "@/lib/services/time-clock-service";
import { MaintenanceService } from "@/lib/services/maintenance";
import type { ScheduleEvent } from "@/lib/types";

export interface ActionDeps {
  messages: AgentMessage[];
  equipmentService: any;
  router: AppRouterInstance;
  currentUser: string | null;
  toast: (opts: { title: string; description: string; variant?: 'default' | 'destructive' | null }) => void;
  setMessages: React.Dispatch<React.SetStateAction<AgentMessage[]>>;
  uow: UnitOfWork;
}

export async function executeHeatherAction(
  msgId: string,
  actionId: string,
  values: Record<string, unknown>,
  deps: ActionDeps,
): Promise<void> {
  const { messages, equipmentService, router, currentUser, toast, setMessages, uow } = deps;

  const asString = (value: unknown): string => (typeof value === "string" ? value : String(value ?? ""));
  const asOptionalString = (value: unknown): string | undefined =>
    value === undefined || value === null || value === "" ? undefined : asString(value);
  const asNumber = (value: unknown, fallback = 0): number => {
    const parsed = Number(value);
    return Number.isFinite(parsed) ? parsed : fallback;
  };
  const asStringArray = (value: unknown): string[] => {
    if (Array.isArray(value)) return value.map(asString).filter(Boolean);
    if (value === undefined || value === null || value === "") return [];
    return [asString(value)];
  };

  // Find original message
  const originalMsg = messages.find((m) => m.id === msgId);
  const parsedContent = parseSmartCard(originalMsg?.content);
  const recipe = parsedContent?.recipe;

  // Handle Print Action immediately
  if (actionId === "print") {
    if (originalMsg?.relatedEntityId) {
      router.push(`/dashboard/inventory/purchase-orders/${originalMsg.relatedEntityId}?print=true`);
      return;
    }
    window.print();
    return;
  }

  // Handle Rejection / Cancellation
  if (
    actionId.toLowerCase().includes("reject") ||
    actionId.toLowerCase().includes("cancel") ||
    actionId.toLowerCase().includes("discard")
  ) {
    const actionBy = currentUser || "user";
    const actionTimestamp = Date.now();

    await agentSync.updateMessageStatus(msgId, "rejected", actionBy);

    setMessages((prev) =>
      prev.map((m) =>
        m.id === msgId ? { ...m, status: "rejected", actionTimestamp, actionBy } : m
      )
    );

    const replyMsg: AgentMessage = {
      id: crypto.randomUUID(),
      createdAt: Date.now(),
      sender: currentUser || "user",
      recipient: "agent",
      status: "sent",
      channel: "direct",
      content: `[ACTION_RESULT: ${actionId}] Status: rejected. Smart card proposal was declined by ${actionBy}.`,
    };
    await agentSync.addMessage(replyMsg);
    toast({ title: "Proposal Rejected", description: "Smart card was declined." });
    return;
  }

  // Visual feedback: Transition to executing
  await agentSync.updateMessageStatus(msgId, "executing");
  setMessages((prev) =>
    prev.map((m) => (m.id === msgId ? { ...m, status: "executing" } : m))
  );

  let newStatus: AgentMessage["status"] = "approved";
  let feedbackNote = "Action completed successfully.";

  // Pre-flight conflict check helper
  const checkConflict = async (collName: keyof typeof uow, entityId: string): Promise<boolean> => {
    if (values.force) return false;
    if (!originalMsg?.snapshotTimestamp) return false;
    const repo = uow[collName] as any;
    if (!repo) return false;
    const current = await repo.get(entityId);
    if (!current) {
      toast({ title: "Conflict", description: "The referenced record no longer exists.", variant: "destructive" });
      return true;
    }
    if (current.updatedAt && current.updatedAt > originalMsg.snapshotTimestamp) {
      toast({
        title: "Stale Data Conflict",
        description: `Heather's info is outdated. ${String(collName)} was updated at ${new Date(current.updatedAt).toLocaleTimeString()}.`,
        variant: "destructive"
      });
      return true;
    }
    return false;
  };

  try {
    const targetService = recipe?.target_service || "";
    const method = recipe?.method || actionId;
    const combinedParams: Record<string, unknown> = {
      ...(recipe?.params ?? {}),
      ...values,
    };

    // =========================================================================
    // 1. FLEET & LOGISTICS TRANSACTIONS (EquipmentService)
    // =========================================================================
    if (actionId === "execute_create_purchase_order" && combinedParams.customerId) {
      const poNo = await equipmentService.createPurchaseOrder(combinedParams);
      feedbackNote = `Purchase Order ${poNo} created successfully.`;
    } else if (actionId === "execute_load_truck" && combinedParams.vehicleId && combinedParams.tankId && combinedParams.inventoryItemId) {
      const vConflict = await checkConflict("vehicles", asString(combinedParams.vehicleId));
      const iConflict = await checkConflict("inventory", asString(combinedParams.inventoryItemId));
      if (vConflict || iConflict) {
        newStatus = "conflict";
        feedbackNote = "Conflict: Vehicle or Inventory was modified after recommendation.";
      } else {
        await equipmentService.loadTruckFromInventory(
          asString(combinedParams.vehicleId),
          asString(combinedParams.tankId),
          asString(combinedParams.inventoryItemId),
          asNumber(combinedParams.amount),
          asOptionalString(combinedParams.workOrderId)
        );
        feedbackNote = `Truck loaded with ${combinedParams.amount} units.`;
      }
    } else if (actionId === "execute_unload_truck" && combinedParams.vehicleId && combinedParams.tankId && combinedParams.inventoryItemId) {
      if (await checkConflict("vehicles", asString(combinedParams.vehicleId))) {
        newStatus = "conflict";
        feedbackNote = "Conflict: Vehicle record is stale.";
      } else {
        await equipmentService.unloadTruckToInventory(
          asString(combinedParams.vehicleId),
          asString(combinedParams.tankId),
          asString(combinedParams.inventoryItemId),
          asNumber(combinedParams.amount)
        );
        feedbackNote = `Unloaded ${combinedParams.amount} units back to inventory lot.`;
      }
    } else if (actionId === "execute_transfer" && combinedParams.sourceId && combinedParams.targetId) {
      if (await checkConflict("vehicles", asString(combinedParams.sourceId))) {
        newStatus = "conflict";
        feedbackNote = "Conflict: Source vehicle was updated after recommendation.";
      } else {
        await equipmentService.transferFluid(
          asString(combinedParams.sourceId),
          asString(combinedParams.sourceType),
          asString(combinedParams.targetId),
          asString(combinedParams.targetType),
          asNumber(combinedParams.amount),
          asOptionalString(combinedParams.sourceSubId),
          asOptionalString(combinedParams.targetSubId)
        );
        feedbackNote = `Transfer of ${combinedParams.amount} units complete.`;
      }
    } else if (actionId === "execute_adjust_inventory" && combinedParams.inventoryItemId) {
      if (await checkConflict("inventory", asString(combinedParams.inventoryItemId))) {
        newStatus = "conflict";
        feedbackNote = "Conflict: Inventory item count has changed.";
      } else {
        await equipmentService.adjustInventoryLot(
          asString(combinedParams.inventoryItemId),
          asNumber(combinedParams.quantity),
          asOptionalString(combinedParams.notes)
        );
        feedbackNote = `Inventory level adjusted to ${combinedParams.quantity}.`;
      }
    } else if (actionId === "execute_add_inventory" && combinedParams.productName) {
      const id = await equipmentService.addInventoryLot({
        productName: asString(combinedParams.productName),
        chemicalId: asOptionalString(combinedParams.chemicalId),
        itemNumber: asOptionalString(combinedParams.itemNumber),
        quantity: asNumber(combinedParams.quantity),
        unit: asString(combinedParams.unit || "GAL"),
        isSupplied: !!combinedParams.isSupplied,
        source: asOptionalString(combinedParams.source),
        lotNumber: asOptionalString(combinedParams.lotNumber),
      });
      feedbackNote = `New lot for ${combinedParams.productName} added to ledger (Lot ID: ${asString(id).slice(0, 8)}...).`;
    } else if (actionId === "execute_dump" && combinedParams.vehicleId) {
      await equipmentService.dumpVessel(
        asString(combinedParams.vehicleId),
        asString(combinedParams.type),
        asOptionalString(combinedParams.subId)
      );
      feedbackNote = `${combinedParams.type} on truck ${combinedParams.vehicleId} has been emptied.`;
    } else if (actionId === "execute_calibrate" && combinedParams.vehicleId && combinedParams.level) {
      await equipmentService.adjustVesselLevel(
        asString(combinedParams.vehicleId),
        asString(combinedParams.type),
        asNumber(combinedParams.level),
        asOptionalString(combinedParams.subId)
      );
      feedbackNote = `Calibrated ${combinedParams.type} to ${combinedParams.level}.`;
    } else if (actionId === "execute_fuel_purchase" && combinedParams.vehicleId) {
      await equipmentService.recordFuelPurchase({
        vehicleId: asString(combinedParams.vehicleId),
        gallons: asNumber(combinedParams.gallons),
        cost: combinedParams.cost ? asNumber(combinedParams.cost) : undefined,
        pricePerUnit: combinedParams.pricePerUnit ? asNumber(combinedParams.pricePerUnit) : undefined,
        type: asOptionalString(combinedParams.type),
        isFull: !!combinedParams.isFull,
        odometer: combinedParams.odometer ? asNumber(combinedParams.odometer) : undefined,
        notes: asOptionalString(combinedParams.notes),
      });
      feedbackNote = `Fuel purchase recorded for Truck ${combinedParams.vehicleId}.`;
    } else if (actionId === "execute_water_refill" && combinedParams.vehicleId) {
      const tankList = asStringArray(combinedParams.tankIds).length > 0
        ? asStringArray(combinedParams.tankIds)
        : asStringArray(combinedParams.tankId);

      await equipmentService.recordWaterRefill({
        vehicleId: asString(combinedParams.vehicleId),
        tankIds: tankList,
        gallons: asNumber(combinedParams.gallons),
        isFull: !!combinedParams.isFull,
        notes: asOptionalString(combinedParams.notes),
        source: asOptionalString(combinedParams.source),
      });
      feedbackNote = `Water tanks refilled on Truck ${combinedParams.vehicleId}.`;
    } else if (actionId === "execute_start_shift" && combinedParams.vehicleId) {
      const id = await equipmentService.startDailyShift({
        vehicleId: asString(combinedParams.vehicleId),
        crew: combinedParams.crew || [{ name: currentUser || "Lead Applicator", role: "Applicator" }],
        startOdometer: asNumber(combinedParams.startOdometer),
      });
      feedbackNote = `Daily shift started for Truck ${combinedParams.vehicleId} (Log: ${id}).`;
    } else if (actionId === "execute_join_mission" && combinedParams.vehicleId && combinedParams.workOrderId) {
      await equipmentService.joinMission(asString(combinedParams.vehicleId), asString(combinedParams.workOrderId));
      feedbackNote = `Truck ${combinedParams.vehicleId} joined Mission ${combinedParams.workOrderId}.`;
    } else if (actionId === "execute_leave_mission" && combinedParams.vehicleId && combinedParams.workOrderId) {
      await equipmentService.leaveMission(asString(combinedParams.vehicleId), asString(combinedParams.workOrderId));
      feedbackNote = `Truck ${combinedParams.vehicleId} left Mission ${combinedParams.workOrderId}.`;
    } else if (actionId === "execute_end_shift" && combinedParams.vehicleId) {
      await equipmentService.endDailyShift(asString(combinedParams.vehicleId), asNumber(combinedParams.endOdometer));
      feedbackNote = `Daily shift ended for Truck ${combinedParams.vehicleId}.`;
    } else if (actionId === "execute_draft_quote") {
      const queryParams = new URLSearchParams({
        bidData: JSON.stringify(combinedParams),
        fromAgent: "true",
      });
      router.push(`${getDashboardCreatePath('bidding')}?${queryParams.toString()}`);
      feedbackNote = "Redirecting to Bidding setup...";

    // =========================================================================
    // 2. MAINTENANCE SERVICE (MaintenanceService)
    // =========================================================================
    } else if (
      targetService === "MaintenanceService" ||
      actionId === "approve_maint_wo" ||
      actionId === "add_line" ||
      actionId === "apply_template" ||
      actionId === "finalize_wo" ||
      actionId === "add_part_usage"
    ) {
      const maint = new MaintenanceService(uow);

      if (actionId === "approve_maint_wo" || method === "createMaintenanceWO") {
        const wo = await maint.createMaintenanceWO({
          vehicleId: asOptionalString(combinedParams.vehicleId),
          vehicleName: asOptionalString(combinedParams.vehicleName),
          vehicleTag: asOptionalString(combinedParams.vehicleTag),
          targetAddonId: asOptionalString(combinedParams.targetAddonId),
          targetAddonName: asOptionalString(combinedParams.targetAddonName),
          standaloneEquipmentId: asOptionalString(combinedParams.standaloneEquipmentId),
          standaloneEquipmentName: asOptionalString(combinedParams.standaloneEquipmentName),
          title: asOptionalString(combinedParams.title),
          technicianName: asOptionalString(combinedParams.technicianName) || currentUser || "Lead Applicator",
          laborRate: combinedParams.laborRate ? asNumber(combinedParams.laborRate) : undefined,
          odometerIn: combinedParams.odometerIn ? asNumber(combinedParams.odometerIn) : undefined,
          hoursIn: combinedParams.hoursIn ? asNumber(combinedParams.hoursIn) : undefined,
        });

        // If a template was specified, apply it immediately
        if (combinedParams.templateId && wo.id) {
          await maint.applyTemplate(wo.id, asString(combinedParams.templateId));
        }

        feedbackNote = `Maintenance Work Order ${wo.maintenanceNumber || wo.id} created successfully.`;
      } else if (actionId === "add_line" || method === "addLaborLine") {
        await maint.addLaborLine(asString(combinedParams.woId), {
          description: asString(combinedParams.description),
          templateId: asOptionalString(combinedParams.templateId),
        });
        feedbackNote = `Labor line added to maintenance work order.`;
      } else if (actionId === "apply_template" || method === "applyTemplate") {
        await maint.applyTemplate(
          asString(combinedParams.woId),
          asString(combinedParams.templateId)
        );
        feedbackNote = `Service template '${combinedParams.templateId}' applied to work order.`;
      } else if (actionId === "finalize_wo" || method === "finalizeWO" || method === "finalizeMaintenanceWO") {
        const finalized = await maint.finalizeWO(asString(combinedParams.woId), {
          createExpense: !!combinedParams.createExpenseRecord,
          shopSupplies: asNumber(combinedParams.shopSupplies),
          odometerOut: combinedParams.odometerOut ? asNumber(combinedParams.odometerOut) : undefined,
          hoursOut: combinedParams.hoursOut ? asNumber(combinedParams.hoursOut) : undefined,
          currentUser: currentUser || undefined,
        });
        feedbackNote = `Maintenance Work Order ${finalized.maintenanceNumber || finalized.id} finalized and archived.`;
      } else if (actionId === "add_part_usage" || method === "addPartToLine") {
        await maint.addPartToLine(asString(combinedParams.woId), asString(combinedParams.lineId), {
          inventoryPartId: asString(combinedParams.inventoryItemId || combinedParams.partId),
          partName: asString(combinedParams.partName),
          partNumber: asString(combinedParams.partNumber || ''),
          quantity: asNumber(combinedParams.quantity, 1),
          unitCost: asNumber(combinedParams.unitCost),
        });
        feedbackNote = `Part '${combinedParams.partName}' added to maintenance line.`;
      }

    // =========================================================================
    // 3. TIME CLOCK TRANSACTIONS (TimeClockService)
    // =========================================================================
    } else if (
      targetService === "TimeClockService" ||
      ["confirm", "clockIn", "clockOut", "startBreak", "endBreak",
       "woClockIn", "woClockOut", "addManualEntry"].includes(actionId) ||
      ["clockIn", "clockOut", "startBreak", "endBreak",
       "woClockIn", "woClockOut", "addManualEntry"].includes(method)
    ) {
      const tc = new TimeClockService(uow);

      if (method === "clockIn") {
        await tc.clockIn(combinedParams);
        feedbackNote = `${combinedParams.employee || "Applicator"} clocked in at ${String(combinedParams.clockIn ?? "").slice(11, 16)}.`;
      } else if (method === "clockOut") {
        await tc.clockOut(combinedParams);
        const reg = asNumber(combinedParams.regularHours);
        const ot = asNumber(combinedParams.otHours);
        feedbackNote = `${combinedParams.employee || "Applicator"} clocked out. Regular: ${reg.toFixed(2)} hrs${ot > 0 ? ` + ${ot.toFixed(2)} hrs OT` : ""}.`;
      } else if (method === "startBreak") {
        await tc.startBreak(combinedParams);
        feedbackNote = `${combinedParams.employee || "Applicator"} started ${(combinedParams.breakType as string) === "lunch" ? "Lunch" : "Break"}.`;
      } else if (method === "endBreak") {
        await tc.endBreak(combinedParams);
        feedbackNote = `${combinedParams.employee || "Applicator"} returned from ${(combinedParams.breakType as string) === "lunch" ? "lunch" : "break"} after ${combinedParams.actualMinutes || "?"} min.`;
      } else if (method === "woClockIn") {
        await tc.woClockIn(combinedParams);
        feedbackNote = `Timer started on WO ${combinedParams.woNumber || combinedParams.woId}.`;
      } else if (method === "woClockOut") {
        const entry = await tc.woClockOut(combinedParams);
        feedbackNote = `Timer stopped on WO ${combinedParams.woNumber || combinedParams.woId}. Duration: ${(entry as any)?.durationMinutes ?? "?"} min.`;
      } else if (method === "addManualEntry") {
        await tc.addManualEntry(combinedParams);
        const entryType = (combinedParams.type as string) === "sick" ? "sick time" : "PTO";
        feedbackNote = `Recorded ${combinedParams.regularHours ?? 8} hrs ${entryType} for ${combinedParams.employee} on ${combinedParams.date}.`;
      } else {
        feedbackNote = `Time clock action '${method}' executed.`;
      }

    // =========================================================================
    // 4. MASTER CALENDAR & SCHEDULE (CalendarService)
    // =========================================================================
    } else if (targetService === "CalendarService" || actionId === "approve_calendar_event") {
      if (actionId === "approve_create" || method === "createEvent") {
        const eventId = crypto.randomUUID();
        const startStr = asString(combinedParams.start || new Date().toISOString());
        const datePart = startStr.slice(0, 10);
        const timePart = startStr.length > 11 ? startStr.slice(11, 16) : undefined;

        const newEvent: ScheduleEvent = {
          id: eventId,
          title: asString(combinedParams.title || "Scheduled Item"),
          date: datePart,
          time: timePart,
          type: (combinedParams.type as any) || "Project Task",
          notes: asOptionalString(combinedParams.description),
          projectId: asOptionalString(combinedParams.projectId) || null,
          projectName: asOptionalString(combinedParams.projectName) || null,
          completed: false,
          createdAt: Date.now(),
        };

        await uow.events.put(newEvent as any);
        feedbackNote = `Event "${newEvent.title}" added to the master calendar on ${datePart}.`;
      } else if (actionId === "approve_update" || method === "updateEvent") {
        const existing = await uow.events.get(asString(combinedParams.id));
        if (existing) {
          await uow.events.put({
            ...existing,
            ...combinedParams,
            updatedAt: Date.now(),
          } as any);
          feedbackNote = `Calendar event "${existing.title}" updated.`;
        } else {
          feedbackNote = "Calendar update failed: Event record not found.";
          newStatus = "error";
        }
      }

    // =========================================================================
    // 5. CUSTOMERS, LOCATIONS, PROJECTS & CONTRACTS
    // =========================================================================
    } else if (actionId === "approve_create" || actionId === "approve_update") {
      const metadata: Record<string, unknown> = (combinedParams.metadata as Record<string, unknown>) ?? {};

      if (targetService === "CustomersService") {
        if (actionId === "approve_create") {
          const id = crypto.randomUUID();
          await uow.customers.put({
            id,
            name: asString(combinedParams.name),
            type: (combinedParams.type as any) ?? "Customer",
            contacts: [],
            phone: asOptionalString(metadata.phone),
            email: asOptionalString(metadata.email),
            addressStreet: asOptionalString(metadata.addressStreet),
            addressCity: asOptionalString(metadata.addressCity),
            addressState: asOptionalString(metadata.addressState),
            addressZip: asOptionalString(metadata.addressZip),
            updatedAt: Date.now(),
          } as any);
          feedbackNote = `Customer "${combinedParams.name}" created successfully.`;
        } else {
          const existing = await uow.customers.get(asString(combinedParams.id));
          if (existing) {
            await uow.customers.put({ ...existing, ...combinedParams, updatedAt: Date.now() } as any);
            feedbackNote = `Customer "${existing.name}" updated.`;
          } else {
            feedbackNote = "Customer update failed: Record not found.";
            newStatus = "error";
          }
        }
      } else if (targetService === "LocationsService") {
        if (actionId === "approve_create") {
          const id = crypto.randomUUID();
          await uow.locations.put({
            id,
            name: asString(combinedParams.name),
            county: asString(combinedParams.county || "Idaho County"),
            roadType: asOptionalString(combinedParams.roadType),
            ...metadata,
            updatedAt: Date.now(),
          } as any);
          feedbackNote = `Location "${combinedParams.name}" added to master library.`;
        } else {
          const existing = await uow.locations.get(asString(combinedParams.id));
          if (existing) {
            await uow.locations.put({ ...existing, ...combinedParams, updatedAt: Date.now() } as any);
            feedbackNote = `Location "${existing.name}" updated.`;
          } else {
            feedbackNote = "Location update failed: Record not found.";
            newStatus = "error";
          }
        }
      } else if (targetService === "ProjectsService" || targetService === "BiddingService" || targetService === "ContractsService") {
        if (actionId === "approve_create") {
          const id = crypto.randomUUID();
          await uow.projects.put({
            id,
            ...combinedParams,
            status: "Draft",
            updatedAt: Date.now(),
          } as any);
          feedbackNote = `Contract/Project "${combinedParams.name ?? combinedParams.title ?? id}" created.`;
        } else {
          const existing = await uow.projects.get(asString(combinedParams.id));
          if (existing) {
            await uow.projects.put({ ...existing, ...combinedParams, updatedAt: Date.now() } as any);
            feedbackNote = `Contract/Project "${existing.name ?? existing.id}" updated.`;
          } else {
            feedbackNote = "Contract/Project update failed: Record not found.";
            newStatus = "error";
          }
        }
      } else {
        feedbackNote = `Service "${targetService}" approved.`;
      }
    } else {
      feedbackNote = `Action "${actionId}" acknowledged.`;
    }

    if (newStatus === "approved") {
      toast({ title: "Action Success", description: feedbackNote });
    }
  } catch (err: unknown) {
    console.error("Action Execution Error:", err);
    newStatus = "error";
    feedbackNote = `Error: ${err instanceof Error ? err.message : "Unknown internal error"}`;
    toast({ title: "Execution Failed", description: feedbackNote, variant: "destructive" });
  }

  // Finalize Message State
  const actionTimestamp = Date.now();
  const actionBy = currentUser || "user";

  setMessages((prev) =>
    prev.map((m) =>
      m.id === msgId ? { ...m, status: newStatus, actionTimestamp, actionBy } : m
    )
  );

  await agentSync.updateMessageStatus(msgId, newStatus, actionBy);

  // Close Conversation Loop: Send structured result back to Heather
  const replyMsg: AgentMessage = {
    id: crypto.randomUUID(),
    createdAt: Date.now(),
    sender: currentUser || "user",
    recipient: "agent",
    status: "sent",
    channel: "direct",
    content: `[ACTION_RESULT: ${actionId}] Status: ${newStatus}. ${feedbackNote}`,
    executedAt: newStatus === "approved" ? Date.now() : undefined,
  };
  await agentSync.addMessage(replyMsg);
}
